import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { cacheGet, cachePut, enqueue, isNetworkError, saveOfflineFile } from "@/lib/offline";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import ViewOnlyBanner from "@/components/ViewOnlyBanner";
import { useCanEdit } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Plus,
  Pencil,
  Trash2,
  Download,
  GripVertical,
  Ship,
  Upload,
  ImageIcon,
  FileImage,
  ExternalLink,
  Sparkles,
  Search,
  Star,
  ArrowLeftRight,
  Check,
  Share2,
  Copy,
  RefreshCcw,
  Shuffle,
  Mail,
  AlertTriangle,
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  History,
} from "lucide-react";
import { useConfirm } from "@/context/ConfirmContext";
import { imageSrc } from "@/pages/Products";
import { formatMoney, CURRENCY_SYMBOLS } from "@/lib/format";
import CompareWithGA from "@/components/CompareWithGA";
import FeatureIconPicker from "@/components/FeatureIconPicker";
import { pickFeatureIcons } from "@/lib/featureIcons";
import { downloadAuthedFile } from "@/lib/downloadAuthedFile";
import { useDraggableReorder } from "@/lib/useDraggableReorder";

const VESSEL_TYPES = [
  "Patrol",
  "Workboat",
  "Fishing",
  "Pilot",
  "Tug",
  "Ferry",
  "Yacht",
  "Cargo",
  "Other",
];

// Editable combobox for vessel "Type". Users can pick a seeded value, pick a
// previously-used custom value (from any existing vessel), OR type a brand-new
// value which becomes available next time a vessel is created.
function TypeCombobox({ value, onChange, suggestions }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value || "");
  const wrapRef = useRef(null);

  // Keep the typed text in sync if the form's value changes externally (e.g.
  // when opening an existing vessel for edit).
  useEffect(() => {
    setQuery(value || "");
  }, [value]);

  // Close the suggestion list on outside click.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? suggestions.filter((s) => s.toLowerCase().includes(q))
    : suggestions;

  const commit = (v) => {
    const cleaned = (v || "").trim();
    setQuery(cleaned);
    onChange(cleaned);
    setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative">
      <Input
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit(query);
          } else if (e.key === "Escape") {
            setOpen(false);
          }
        }}
        placeholder="Workboat, Pilot, or type your own…"
        className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
        data-testid="vessel-type-input"
      />
      {open && filtered.length > 0 && (
        <div
          className="absolute z-50 mt-1 w-full max-h-64 overflow-auto rounded-md border border-[#DDE5EC] bg-white py-1 shadow-lg"
          data-testid="vessel-type-suggestions"
        >
          {filtered.map((t) => {
            const isMatch = t.toLowerCase() === q;
            return (
              <button
                type="button"
                key={t}
                onMouseDown={(e) => {
                  // mousedown (not click) so the input doesn't lose focus first
                  e.preventDefault();
                  commit(t);
                }}
                className={`flex w-full items-center justify-between px-3 py-1.5 text-left text-sm text-[#011836] hover:bg-white ${
                  isMatch ? "bg-white" : ""
                }`}
                data-testid={`vessel-type-option-${t}`}
              >
                <span>{t}</span>
                {value && value.toLowerCase() === t.toLowerCase() && (
                  <span className="text-[#0088CC]">✓</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
const CURRENCIES = Object.keys(CURRENCY_SYMBOLS);

const DEFAULT_FEATURES = [
  { title: "Built to Last", description: "Robust hull and structure engineered for heavy-duty operations." },
  { title: "Optimised Operations", description: "Spacious open deck layout optimised for cargo, equipment, and flexibility." },
  { title: "Lower Operating Costs", description: "Fuel efficient design and cost-effective performance." },
  { title: "Ease of Maintenance", description: "Designed for simple maintenance and maximum uptime." },
  { title: "Versatile Platform", description: "Ideal for construction support, logistics, towing, and offshore project work." },
];

// Pick up to 3 Principal Particulars to feature on the vessel card.
// Admin can flag individual rows with `is_highlighted: true` — those win.
// If fewer than 3 are flagged (or none), fill the slot with the first
// enabled, non-placeholder rows in display order so the card never goes
// blank. Returns an array of `{label, value}` up to 3 entries long.
function highlightedParticulars(v) {
  const list = v?.principal_particulars || [];
  const isUsable = (p) => {
    if (!p || p.enabled === false) return false;
    // Section headings carry no value and never belong on the card.
    if ((p.kind || "row") === "heading") return false;
    const val = (p.value || "").toString().trim().toLowerCase();
    if (!val) return false;
    return !["unknown", "tbc", "tba", "n/a", "na", "-", "—", "?"].includes(val);
  };
  const usable = list.filter(isUsable);
  const flagged = usable.filter((p) => p.is_highlighted === true).slice(0, 3);
  if (flagged.length >= 3) return flagged;
  const flaggedKeys = new Set(flagged.map((p) => `${p.label}|${p.value}`));
  const fillers = usable.filter(
    (p) => !flaggedKeys.has(`${p.label}|${p.value}`)
  );
  return [...flagged, ...fillers].slice(0, 3);
}

const empty = {
  name: "",
  sub_category: "",
  client_id: null,
  vessel_type: "",
  material: "",
  notes: "",
  price: "",
  price_currency: "USD",
  tagline: "",
  description: "",
  render_path: null,
  ga_path: null,
  ga_preview_path: null,
  ga_rotation: 0,
  render_zoom: 1.10,
  render_offset_x: 0,
  render_offset_y: 0,
  key_features: [...DEFAULT_FEATURES],
  principal_particulars: [],
  // New vessels start as drafts — admins click "Go Live" once the
  // record is fully prepped (hero render, description, particulars).
  is_live: false,
};

// Poll a background AI job until it's done or fails. The kicker endpoints
// (/vessels/ai/describe, /vessels/ai/refine) return a `job_id` immediately;
// we then ping /ai-jobs/<id> every 2.5s until status leaves "pending".
// Why this exists: Cloudflare + Emergent's upstream proxy on production cut
// connections older than ~45s, so we can't wait for the LLM call inline.
async function pollAiJob(jobId, { maxWaitMs = 5 * 60 * 1000, intervalMs = 2500 } = {}) {
  const started = Date.now();
  while (Date.now() - started < maxWaitMs) {
    await new Promise((r) => setTimeout(r, intervalMs));
    let job;
    try {
      const { data } = await api.get(`/ai-jobs/${jobId}`);
      job = data;
    } catch (err) {
      // Transient network blip — keep polling unless the server returned 404.
      if (err?.response?.status === 404) {
        throw new Error("AI job expired before it could complete");
      }
      continue;
    }
    if (job.status === "done") return job.result;
    if (job.status === "failed") {
      throw new Error(job.error || "AI job failed");
    }
  }
  throw new Error("AI job timed out (waited 5 minutes)");
}

// AuditPanel — collapsible badge bar at the top of the vessel editor that
// surfaces every issue the server-side audit would flag against the
// current in-flight form. The override checkbox + Save gate live here
// so all data-quality UX is in one place.
function AuditPanel({ audit, collapsed, onToggleCollapse }) {
  if (!audit) return null;
  const sev = audit.severity;
  const styles = {
    high: {
      border: "border-[#EF4444]/50",
      bg: "bg-[#FDECEC]",
      icon: AlertTriangle,
      iconCls: "text-[#EF4444]",
      label: "Data quality — needs review",
    },
    medium: {
      border: "border-[#FBBF24]/40",
      bg: "bg-[#FFF7E6]",
      icon: AlertCircle,
      iconCls: "text-[#FBBF24]",
      label: "Data quality — heads up",
    },
    low: {
      border: "border-[#C4D0DB]",
      bg: "bg-[#F6F7F8]",
      icon: AlertCircle,
      iconCls: "text-[#5B6B7C]",
      label: "Data quality — minor notes",
    },
    ok: {
      border: "border-[#10B981]/40",
      bg: "bg-[#E7F6EE]",
      icon: CheckCircle2,
      iconCls: "text-[#10B981]",
      label: "Data quality — clean",
    },
  }[sev] || {};
  const Icon = styles.icon;
  const issueCount = (audit.issues || []).filter((i) => !i.dismissed).length;
  const badgeCls = {
    high: "bg-[#FDECEC] text-[#EF4444] border-[#EF4444]/40",
    medium: "bg-[#FFF7E6] text-[#FBBF24] border-[#FBBF24]/40",
    low: "bg-[#EDF1F5] text-[#5B6B7C] border-[#C4D0DB]",
  };

  return (
    <div
      className={`rounded-lg border ${styles.border} ${styles.bg}`}
      data-testid="vessel-audit-panel"
      data-severity={sev}
    >
      <button
        type="button"
        onClick={onToggleCollapse}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
        data-testid="vessel-audit-toggle"
      >
        <div className="flex items-center gap-2 min-w-0">
          {Icon && <Icon className={`h-4 w-4 shrink-0 ${styles.iconCls}`} />}
          <span className="font-display text-sm font-semibold text-[#011836] truncate">
            {styles.label}
          </span>
          {issueCount > 0 && (
            <span className="text-[10px] uppercase tracking-wider text-[#5B6B7C] font-display ml-1">
              {issueCount} {issueCount === 1 ? "issue" : "issues"}
            </span>
          )}
        </div>
        {collapsed ? (
          <ChevronDown className="h-4 w-4 text-[#5B6B7C] shrink-0" />
        ) : (
          <ChevronUp className="h-4 w-4 text-[#5B6B7C] shrink-0" />
        )}
      </button>

      {!collapsed && issueCount > 0 && (
        <ul className="px-4 pb-3 space-y-1.5" data-testid="vessel-audit-issues">
          {audit.issues.filter((i) => !i.dismissed).map((issue, i) => (
            <li key={i} className="text-sm text-[#33475C] flex gap-2">
              <span
                className={`text-[10px] uppercase tracking-wider font-display font-semibold px-1.5 py-0.5 rounded border shrink-0 mt-0.5 ${badgeCls[issue.severity] || ""}`}
              >
                {issue.severity}
              </span>
              <span>{issue.msg}</span>
            </li>
          ))}
        </ul>
      )}

      {sev === "high" && (
        <div className="px-4 pb-3 pt-1 border-t border-[#EF4444]/20">
          <p className="text-xs text-[#5B6B7C]" data-testid="vessel-audit-advisory-note">
            Saving is <span className="text-[#011836] font-medium">not blocked</span> — these are
            flags to cross-check against the GA. Unresolved issues stay listed on the{" "}
            <span className="text-[#0088CC]">Vessels → Audit</span> page until you fix them
            or mark them verified there.
          </p>
        </div>
      )}
    </div>
  );
}



export default function Vessels() {
  const { user, refresh } = useAuth();
  const isAdmin = user?.role === "admin";
  const isClient = user?.role === "client";
  const canEdit = useCanEdit();
  // Client enquiry modal — opened when a client clicks "Request quote" on
  // a vessel card. Submission saves to the admin's inbox on the Dashboard.
  const [enquiryModal, setEnquiryModal] = useState({
    open: false,
    vessel: null,
    message: "",
    submitting: false,
  });
  const openEnquiry = (vessel) =>
    setEnquiryModal({ open: true, vessel, message: "", submitting: false });
  const closeEnquiry = () => setEnquiryModal((m) => ({ ...m, open: false }));
  const submitEnquiry = async () => {
    if (!enquiryModal.vessel) return;
    setEnquiryModal((m) => ({ ...m, submitting: true }));
    try {
      await api.post("/quote-requests", {
        vessel_id: enquiryModal.vessel.id,
        message: enquiryModal.message,
      });
      toast.success("Thanks — our team will be in touch shortly");
      closeEnquiry();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not send enquiry");
      setEnquiryModal((m) => ({ ...m, submitting: false }));
    }
  };

  const confirm = useConfirm();
  const [list, setList] = useState([]);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("All");
  // Construction material filter — chips alongside vessel-type chips so
  // customers can narrow the fleet down to Steel- or Aluminium-built
  // models. "All" disables the filter.
  const [materialFilter, setMaterialFilter] = useState("All");
  const [favouritesOnly, setFavouritesOnly] = useState(false);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [uploadingKey, setUploadingKey] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  // Refine flow — lets the user nudge the AI when the generated copy is wrong.
  // Only touches description + key_features; principal_particulars and the
  // numeric fields above are intentionally preserved.
  const [refineFeedback, setRefineFeedback] = useState("");
  const [refineLoading, setRefineLoading] = useState(false);
  // AI hero generation from the uploaded GA (Gemini Nano Banana image-to-image).
  const [heroGenerating, setHeroGenerating] = useState(false);
  // Single textarea drives both the first-time style hint AND the post-generation
  // refine feedback — the prompt path differs based on whether a render already exists.
  const [heroFeedback, setHeroFeedback] = useState("");
  // Social post generator (admin-only) — per-vessel modal.
  const [socialModal, setSocialModal] = useState({
    open: false,
    vessel: null,
    platform: "linkedin",
    feedback: "",
    loading: false,
    text: "",
    hashtags: [],
  });
  // Per-vessel change history modal (staff + admin).
  const [historyModal, setHistoryModal] = useState({
    open: false,
    vessel: null,
    entries: [],
    loading: false,
  });
  const openHistory = async (v) => {
    setHistoryModal({ open: true, vessel: v, entries: [], loading: true });
    try {
      const { data } = await api.get(`/vessels/${v.id}/history`);
      setHistoryModal((m) => ({ ...m, entries: data, loading: false }));
    } catch (e) {
      toast.error(formatApiError(e));
      setHistoryModal((m) => ({ ...m, loading: false }));
    }
  };
  // Live audit panel — runs `POST /api/vessels/audit-preview` against the
  // in-flight form so the admin sees the same issues the global audit
  // dashboard would surface BEFORE they hit Save. Advisory only — saving
  // is never blocked by content issues (only the physical 11-row flyer
  // cap still blocks server-side). ``debounceRef`` coalesces keystrokes.
  const [audit, setAudit] = useState(null);

  const [auditCollapsed, setAuditCollapsed] = useState(false);
  // "Compare with GA" review modal — opened by a button in the editor when
  // a GA is uploaded. Lets the admin scan AI-extracted PP/KF against the
  // original drawing side-by-side to catch misreads.
  const [compareOpen, setCompareOpen] = useState(false);
  const auditDebounceRef = useRef(null);

  // Drag-to-reorder for the Principal Particulars list — admins frequently
  // want LOA first, ENGINES last, etc. The hook owns the dragging /
  // hovered-row indices so the row markup can show a subtle indicator.
  const ppReorder = useDraggableReorder(
    form.principal_particulars || [],
    (next) => setForm((f) => ({ ...f, principal_particulars: next })),
  );
  const renderRef = useRef(null);
  const gaRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      // /clients requires the `clients` permission which view-only staff
      // (and even some editor staff) don't have. /vessels is the critical
      // call — the client-link dropdown in the edit dialog is a nice-to-
      // have. Use allSettled so a 403 on /clients doesn't kill the whole
      // page with a misleading "could not load vessels" error.
      const needClients = user?.role !== "client";
      const [v, c] = await Promise.allSettled([
        api.get("/vessels"),
        needClients ? api.get("/clients") : Promise.resolve({ data: [] }),
      ]);
      if (v.status === "fulfilled") {
        setList(v.value.data);
        cachePut("vessels", v.value.data);
      } else if (!v.reason?.response) {
        // Network error (offline) — fall back to the last saved copy.
        const cached = await cacheGet("vessels");
        if (cached) {
          setList(cached);
          toast.message("Offline — showing your saved vessels");
        } else {
          toast.error("Could not load vessels — please refresh");
        }
      } else if (v.reason?.response?.status && v.reason.response.status !== 401) {
        // Real vessels-load failure — surface it.
        toast.error("Could not load vessels — please refresh");
      }
      if (c.status === "fulfilled") {
        setClients(c.value.data);
      }
      // c.status === "rejected" is fine — clients dropdown just stays empty.
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Deep link from the audit page ("Open editor" → /vessels?focus={id}):
  // once vessels load, open that vessel's editor and clear the param.
  // Remember the origin so a successful save returns to the audit page.
  const [searchParams, setSearchParams] = useSearchParams();
  const [returnToAudit, setReturnToAudit] = useState(false);
  useEffect(() => {
    const focusId = searchParams.get("focus");
    if (!focusId || !list.length) return;
    const target = list.find((v) => v.id === focusId);
    if (target) {
      setReturnToAudit(true);
      openEdit(target);
    } else toast.error("Vessel not found");
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list, searchParams]);

// Standard PP template for new vessels — matches the backend's
// PP_TEMPLATE (server.py) used to migrate existing vessels.
const PP_TEMPLATE_ROWS = () => [
  ["Length", "row"],
  ["Breadth", "row"],
  ["Draft", "row"],
  ["Design Displacement", "row"],
  ["Speed", "row"],
  ["Machinery", "heading"],
  ["Main Engine", "row"],
  ["Tank Capacity", "heading"],
  ["Diesel Capacity", "row"],
  ["Fresh Water Capacity", "row"],
  ["Load Capacity", "heading"],
  ["Complement", "row"],
].map(([label, kind]) => ({
  label,
  value: "",
  kind,
  enabled: kind === "row",
  is_highlighted: false,
}));

  const openNew = () => {
    setEditing(null);
    setForm({
      ...empty,
      key_features: [...DEFAULT_FEATURES],
      principal_particulars: PP_TEMPLATE_ROWS(),
    });
    setAudit(null);

    setAuditCollapsed(false);
    setOpen(true);
  };

  const openEdit = (v) => {
    setEditing(v);
    setForm({
      ...empty,
      ...v,
      price: v.price ?? "",
      price_currency: v.price_currency || "USD",
      key_features: v.key_features?.length ? v.key_features : [...DEFAULT_FEATURES],
      principal_particulars: (v.principal_particulars || []).map((sp) => ({
        ...sp,
        kind: sp.kind === "heading" ? "heading" : "row",
        enabled: sp.enabled !== false,
        is_highlighted: !!sp.is_highlighted,
      })),
    });
    setAudit(null);

    setAuditCollapsed(false);
    setOpen(true);
  };

  const uploadImage = async (field, file, opts = {}) => {
    setUploadingKey(field);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => {
        const next = { ...f, [field]: data.path };
        if (opts.previewField) next[opts.previewField] = data.preview_path || data.path;
        return next;
      });
      toast.success(opts.previewField && file.type === "application/pdf" ? "PDF uploaded — preview generated" : "Uploaded");
    } catch (err) {
      if (isNetworkError(err) && file.type !== "application/pdf") {
        const placeholder = await saveOfflineFile(file);
        setForm((f) => {
          const next = { ...f, [field]: placeholder };
          if (opts.previewField) next[opts.previewField] = placeholder;
          return next;
        });
        toast.success("Image saved offline — it will upload when you're back online");
      } else if (isNetworkError(err)) {
        toast.error("PDF uploads need a connection — please try again when you're back online");
      } else {
        toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
      }
    } finally {
      setUploadingKey(null);
    }
  };

  const onRenderPick = (e) => {
    const f = e.target.files?.[0];
    if (f) uploadImage("render_path", f, { previewField: null });
    if (renderRef.current) renderRef.current.value = "";
  };
  const onGaPick = (e) => {
    const f = e.target.files?.[0];
    if (f) uploadImage("ga_path", f, { previewField: "ga_preview_path" });
    if (gaRef.current) gaRef.current.value = "";
  };

  const [regenerating, setRegenerating] = useState(false);
  const regenerateGaPreview = async () => {
    if (!editing?.id || regenerating) return;
    setRegenerating(true);
    try {
      const { data } = await api.post(`/vessels/${editing.id}/regenerate-ga-preview`);
      setForm((f) => ({ ...f, ga_preview_path: data.ga_preview_path }));
      toast.success("GA preview regenerated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not regenerate preview");
    } finally {
      setRegenerating(false);
    }
  };

  const updateFeature = (i, patch) =>
    setForm((f) => {
      const arr = [...f.key_features];
      arr[i] = { ...arr[i], ...patch };
      return { ...f, key_features: arr };
    });
  const removeFeature = (i) =>
    setForm((f) => ({ ...f, key_features: f.key_features.filter((_, x) => x !== i) }));
  const addFeature = () =>
    setForm((f) => ({ ...f, key_features: [...f.key_features, { title: "", description: "" }] }));

  const updateSpec = (i, patch) =>
    setForm((f) => {
      const arr = [...f.principal_particulars];
      arr[i] = { ...arr[i], ...patch };
      return { ...f, principal_particulars: arr };
    });
  const removeSpec = (i) =>
    setForm((f) => ({ ...f, principal_particulars: f.principal_particulars.filter((_, x) => x !== i) }));
  const addSpec = () =>
    setForm((f) => ({
      ...f,
      principal_particulars: [
        ...f.principal_particulars,
        { kind: "row", label: "", value: "", enabled: true, is_highlighted: false },
      ],
    }));
  const addHeading = () =>
    setForm((f) => ({
      ...f,
      principal_particulars: [
        ...f.principal_particulars,
        // Section dividers carry only a label. The form layout hides
        // the value / Flyer / Card columns when kind === "heading".
        // ``enabled: true`` keeps the heading visible on the flyer by
        // default; the admin can untick it to hide a section group.
        { kind: "heading", label: "", value: "", enabled: true, is_highlighted: false },
      ],
    }));

  // Count how many Principal Particulars rows have BOTH a label and a value —
  // the AI describe endpoint requires at least 3 confirmed entries before it
  // will generate anything (new workflow: admin owns PP, AI quotes them).
  // Heading rows are admin-managed section dividers and never reach the AI,
  // so they do NOT count toward this gate.
  const confirmedPpCount = (form.principal_particulars || []).filter(
    (sp) => (sp.kind || "row") !== "heading"
      && (sp.label || "").trim()
      && (sp.value || "").trim()
  ).length;
  const aiDescribeDisabled =
    aiLoading ||
    (!form.render_path && !form.ga_path) ||
    confirmedPpCount < 3;
  const aiDescribeBlockReason = (() => {
    if (!form.render_path && !form.ga_path) return "Upload a render or GA first.";
    if (confirmedPpCount < 3) {
      return (
        `Enter at least 3 Principal Particulars (LOA, BEAM, ENGINES, etc.) first — ` +
        `the AI uses them as authoritative facts. ${confirmedPpCount}/3 confirmed.`
      );
    }
    return "";
  })();

  // Build the PP payload once — same shape for describe + shuffle.
  const aiDescribePayload = () => ({
    name: form.name,
    sub_category: form.sub_category,
    render_path: form.render_path,
    ga_path: form.ga_path,
    ga_preview_path: form.ga_preview_path,
    principal_particulars: (form.principal_particulars || [])
      .filter((sp) => (sp.label || "").trim() && (sp.value || "").trim())
      .map((sp) => ({
        label: sp.label,
        value: sp.value,
        enabled: sp.enabled !== false,
        is_highlighted: !!sp.is_highlighted,
      })),
  });

  const runAiDescribe = async () => {
    if (aiDescribeBlockReason) {
      toast.error(aiDescribeBlockReason);
      return;
    }
    setAiLoading(true);
    try {
      const { data: kick } = await api.post("/vessels/ai/describe", aiDescribePayload());
      const data = await pollAiJob(kick.job_id);
      const incomingFeatures = (data.key_features || []).filter(
        (kf) => kf.title || kf.description,
      );
      // Workflow change (Feb 2026): the AI no longer returns Principal
      // Particulars OR auto-allocated numeric fields. The admin's PP table
      // is the single source of truth and we never overwrite it.
      setForm((f) => ({
        ...f,
        description: data.description || f.description,
        key_features: incomingFeatures.length ? incomingFeatures : f.key_features,
      }));
      const parts = [];
      if (data.description) parts.push("description");
      if (incomingFeatures.length) parts.push(`${incomingFeatures.length} features`);
      toast.success(`AI generated ${parts.join(" + ") || "results"}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail ? formatApiError(detail) : (err?.message || "AI generation failed"));
    } finally {
      setAiLoading(false);
    }
  };

  // "Shuffle" — re-run the describe job for a fresh opener angle. Same
  // gating + same payload as the main describe call. The PP table never
  // changes between re-rolls anyway (it's admin-owned now), so this just
  // swaps in the new description + key_features.
  const runAiShuffle = async () => {
    if (aiDescribeBlockReason) {
      toast.error(aiDescribeBlockReason);
      return;
    }
    setAiLoading(true);
    try {
      const { data: kick } = await api.post("/vessels/ai/describe", aiDescribePayload());
      const data = await pollAiJob(kick.job_id);
      const incomingFeatures = (data.key_features || []).filter(
        (kf) => kf.title || kf.description,
      );
      setForm((f) => ({
        ...f,
        description: data.description || f.description,
        key_features: incomingFeatures.length ? incomingFeatures : f.key_features,
      }));
      toast.success("New variant generated");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail ? formatApiError(detail) : (err?.message || "AI generation failed"));
    } finally {
      setAiLoading(false);
    }
  };

  const runAiRefine = async () => {
    const feedback = refineFeedback.trim();
    if (!feedback) {
      toast.error("Tell the AI what to change");
      return;
    }
    if (!form.description && (!form.key_features || form.key_features.length === 0)) {
      toast.error("Generate the description first, then refine it");
      return;
    }
    setRefineLoading(true);
    try {
      const { data: kick } = await api.post("/vessels/ai/refine", {
        name: form.name,
        sub_category: form.sub_category,
        render_path: form.render_path,
        ga_path: form.ga_path,
        ga_preview_path: form.ga_preview_path,
        current_description: form.description || "",
        current_key_features: (form.key_features || []).map((f) => ({
          title: f.title || "",
          description: f.description || "",
        })),
        feedback,
      });
      const data = await pollAiJob(kick.job_id);
      const incomingFeatures = (data.key_features || []).filter(
        (kf) => kf.title || kf.description,
      );
      setForm((f) => ({
        ...f,
        description: data.description || f.description,
        key_features: incomingFeatures.length ? incomingFeatures : f.key_features,
      }));
      setRefineFeedback("");
      toast.success("AI applied your feedback");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail ? formatApiError(detail) : (err?.message || "Refine failed"));
    } finally {
      setRefineLoading(false);
    }
  };

  // ---------------------------------------------------------------- AI Hero From GA
  // Asks Gemini Nano Banana to render a brand-styled hero image based on the
  // uploaded GA drawing. Replaces the current Hero / Render slot on success
  // (with confirmation if there's already an image there).
  const generateHeroFromGa = async () => {
    if (!form.ga_path && !form.ga_preview_path) {
      toast.error("Upload a GA drawing first");
      return;
    }
    const feedback = heroFeedback.trim();
    // Only show the overwrite confirm on the FIRST generation (no feedback,
    // existing render). When the user is iteratively refining an existing
    // render they obviously want it replaced — skipping the prompt removes friction.
    if (form.render_path && !feedback) {
      const ok = window.confirm(
        "This will replace the current Hero / Render with an AI-generated image. Continue?",
      );
      if (!ok) return;
    }
    setHeroGenerating(true);
    try {
      const { data: kick } = await api.post("/vessels/ai/generate-hero", {
        name: form.name,
        sub_category: form.sub_category,
        ga_path: form.ga_path,
        ga_preview_path: form.ga_preview_path,
        style_hint: feedback,
        current_render_path: form.render_path || null,
      });
      const result = await pollAiJob(kick.job_id, { maxWaitMs: 4 * 60 * 1000 });
      if (!result?.render_path) {
        throw new Error("AI did not return a render — try again");
      }
      setForm((f) => ({ ...f, render_path: result.render_path }));
      // Keep the feedback field populated so the user can tweak and iterate
      // multiple times without retyping. Clear only the success message.
      toast.success(feedback ? "AI refined the hero — review and save" : "AI hero generated — review and save the vessel");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail ? formatApiError(detail) : (err?.message || "Hero generation failed"));
    } finally {
      setHeroGenerating(false);
    }
  };

  // ---------------------------------------------------------------- Social Post (admin)
  const openSocial = (v) => {
    setSocialModal({
      open: true,
      vessel: v,
      platform: "linkedin",
      feedback: "",
      loading: false,
      text: "",
      hashtags: [],
    });
  };
  const closeSocial = () =>
    setSocialModal((m) => ({ ...m, open: false }));

  const generateSocial = async (overrides = {}) => {
    const v = socialModal.vessel;
    if (!v) return;
    const platform = overrides.platform ?? socialModal.platform;
    const feedback = overrides.feedback ?? socialModal.feedback;
    setSocialModal((m) => ({ ...m, loading: true, platform, feedback }));
    try {
      const { data } = await api.post(`/vessels/${v.id}/ai/social-post`, {
        platform,
        feedback: feedback || "",
      });
      setSocialModal((m) => ({
        ...m,
        loading: false,
        text: data.text || "",
        hashtags: data.hashtags || [],
      }));
    } catch (err) {
      setSocialModal((m) => ({ ...m, loading: false }));
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't generate post");
    }
  };

  const copySocial = async () => {
    const combined = (
      (socialModal.text || "") +
      ((socialModal.hashtags && socialModal.hashtags.length)
        ? "\n\n" + socialModal.hashtags.join(" ")
        : "")
    ).trim();
    if (!combined) {
      toast.error("Nothing to copy yet");
      return;
    }
    try {
      await navigator.clipboard.writeText(combined);
      toast.success("Post copied to clipboard");
    } catch {
      window.prompt("Copy this post:", combined);
    }
  };

  // Live audit preview — runs every time the description / key features /
  // principal particulars change, with a 600ms debounce so we don't hit
  // the backend on every keystroke. Skip when the dialog is closed so we
  // don't waste a request while the user isn't editing.
  useEffect(() => {
    if (!open) return undefined;
    if (auditDebounceRef.current) clearTimeout(auditDebounceRef.current);
    auditDebounceRef.current = setTimeout(async () => {
      try {
        const payload = {
          name: form.name || "Unnamed",
          vessel_type: form.vessel_type || "",
          sub_category: form.sub_category || "",
          material: form.material || "",
          description: form.description || "",
          key_features: (form.key_features || []).filter(
            (kf) => (kf.title || "").trim() || (kf.description || "").trim()
          ),
          principal_particulars: (form.principal_particulars || []).filter(
            (sp) => (sp.label || "").trim() || (sp.value || "").trim()
          ),
          is_live: !!form.is_live,
        };
        const qs = editing ? `?vessel_id=${editing.id}` : "";
        const { data } = await api.post(`/vessels/audit-preview${qs}`, payload);
        setAudit(data);
      } catch {
        // Non-fatal — the audit is advisory only.
        setAudit(null);
      }
    }, 600);
    return () => clearTimeout(auditDebounceRef.current);
  }, [
    open,
    editing,
    form.name,
    form.description,
    form.key_features,
    form.principal_particulars,
  ]);

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    const payload = {
        ...form,
        client_id: form.client_id || null,
        price: form.price === "" ? null : Number(form.price),
        key_features: form.key_features.filter((kf) => kf.title.trim() || kf.description.trim()),
        principal_particulars: form.principal_particulars
          // Keep heading rows even if their value is blank (they only
          // have a label). For data rows, require label OR value.
          .filter((s) => {
            const isHeading = (s.kind || "row") === "heading";
            if (isHeading) return s.label.trim();
            return s.label.trim() || s.value.trim();
          })
          .map((s) => {
            const isHeading = (s.kind || "row") === "heading";
            return {
              kind: isHeading ? "heading" : "row",
              label: s.label,
              // Headings carry no value / no highlight; the ``enabled``
              // flag IS honoured for headings too — admins can untick
              // "Flyer" on a heading to hide that section divider on a
              // per-vessel basis without deleting the row.
              value: isHeading ? "" : s.value,
              enabled: s.enabled !== false,
              is_highlighted: isHeading ? false : !!s.is_highlighted,
            };
          }),
      };
    try {
      if (editing) await api.put(`/vessels/${editing.id}`, payload);
      else await api.post(`/vessels`, payload);
      toast.success(editing ? "Vessel updated" : "Vessel created");
      setOpen(false);
      // Came from the audit page? Head back there — it re-runs the audit
      // on mount so the fix is reflected immediately.
      if (returnToAudit) {
        setReturnToAudit(false);
        navigate("/vessels/audit");
        return;
      }
      load();
    } catch (e) {
      if (isNetworkError(e)) {
        const tempId = editing ? null : `temp-${Date.now()}`;
        await enqueue(
          editing
            ? { type: "update_vessel", targetId: editing.id, payload }
            : { type: "create_vessel", tempId, payload },
        );
        const nextList = editing
          ? list.map((v) => (v.id === editing.id ? { ...v, ...payload } : v))
          : [...list, { id: tempId, ...payload, _offline: true }];
        setList(nextList);
        cachePut("vessels", nextList);
        toast.success("Saved offline — will sync when you're back online");
        setOpen(false);
        return;
      }
      const detail = e.response?.data?.detail;
      // Backend 422 — only physical layout constraints block now (e.g.
      // the 11-row flyer cap). Surface the reason inline.
      if (e.response?.status === 422 && detail && detail.code === "audit_hard_block") {
        setAudit(detail.audit);
        setAuditCollapsed(false);
        toast.error(detail.message || "Save blocked — see the note at the top.");
        return;
      }
      toast.error(formatApiError(detail));
    }
  };

  const remove = async (v) => {
    const ok = await confirm({
      title: `Delete vessel ${v.name}?`,
      description: "This action cannot be undone.",
      confirmText: "Delete",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/vessels/${v.id}`);
      toast.success("Vessel deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const clientName = (id) => clients.find((c) => c.id === id)?.company_name || "—";

  const toggleFavourite = async (v) => {
    // Clients have their own per-user favourites list (separate from the
    // admin-global `is_favourite` flag), so we hit a different endpoint
    // and resolve to a local "is favourite for me" flag.
    if (user?.role === "client") {
      try {
        const { data } = await api.post(`/client/favourites/${v.id}`);
        await refresh();
        toast.success(data.is_favourite ? `Added ${v.name} to favourites` : `Removed ${v.name} from favourites`);
      } catch {
        toast.error("Could not update favourite");
      }
      return;
    }
    // Admin / staff path — flips the global flag.
    setList((cur) => cur.map((x) => (x.id === v.id ? { ...x, is_favourite: !x.is_favourite } : x)));
    try {
      const { data } = await api.post(`/vessels/${v.id}/favourite`);
      setList((cur) => cur.map((x) => (x.id === v.id ? data : x)));
    } catch (e) {
      // Roll back
      setList((cur) => cur.map((x) => (x.id === v.id ? { ...x, is_favourite: v.is_favourite } : x)));
      toast.error("Could not update favourite");
    }
  };

  // Flip publish state. Admin-only on the backend; we still hide the UI
  // for staff to avoid a forbidden round-trip.
  const toggleLive = async (v) => {
    const nextState = !v.is_live;
    setList((cur) => cur.map((x) => (x.id === v.id ? { ...x, is_live: nextState } : x)));
    try {
      const { data } = await api.post(`/vessels/${v.id}/live`);
      setList((cur) => cur.map((x) => (x.id === v.id ? data : x)));
      toast.success(nextState ? `${v.name} is now live` : `${v.name} hidden from staff`);
    } catch (e) {
      setList((cur) => cur.map((x) => (x.id === v.id ? { ...x, is_live: v.is_live } : x)));
      toast.error(formatApiError(e.response?.data?.detail) || "Could not toggle visibility");
    }
  };

  // Derived: unique vessel-type options pulled from the data for the filter bar.
  const typeOptions = ["All", ...Array.from(new Set(list.map((v) => v.vessel_type).filter(Boolean)))];
  // Derived: unique material options. Title-case the value so "aluminium"
  // and "Aluminium" don't render as two distinct chips.
  const materialOptions = (() => {
    const seen = new Set();
    list.forEach((v) => {
      const m = (v.material || "").trim();
      if (!m) return;
      const titled = m.charAt(0).toUpperCase() + m.slice(1).toLowerCase();
      seen.add(titled);
    });
    return ["All", ...Array.from(seen).sort()];
  })();
  const lcSearch = search.trim().toLowerCase();
  const [compare, setCompare] = useState([]);
  const toggleCompare = (id) =>
    setCompare((c) => {
      if (c.includes(id)) return c.filter((x) => x !== id);
      if (c.length >= 3) {
        toast.error("You can compare up to 3 vessels");
        return c;
      }
      return [...c, id];
    });

  const filtered = list.filter((v) => {
    if (favouritesOnly && !v.is_favourite) return false;
    if (typeFilter !== "All" && (v.vessel_type || "") !== typeFilter) return false;
    if (
      materialFilter !== "All" &&
      (v.material || "").trim().toLowerCase() !== materialFilter.toLowerCase()
    ) {
      return false;
    }
    if (lcSearch) {
      const hay = `${v.name} ${v.sub_category || ""} ${v.vessel_type || ""} ${v.material || ""}`.toLowerCase();
      if (!hay.includes(lcSearch)) return false;
    }
    return true;
  });

  return (
    <div className="p-4 sm:p-8 lg:p-10 max-w-[1400px] mx-auto pb-24">
      <ViewOnlyBanner />
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6 sm:mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.35em] text-[#0088CC] mb-2">Fleet</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#011836]">
            Vessels
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <Link
              to="/vessels/audit"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-[#C4D0DB] text-sm text-[#011836] hover:bg-[#EDF1F5] font-display tracking-wide"
              data-testid="vessels-audit-link"
              title="Scan all vessels for data-quality issues"
            >
              <AlertTriangle className="h-4 w-4 text-[#FBBF24]" /> Data audit
            </Link>
          )}
          {!isClient && canEdit && (
            <Button
              onClick={openNew}
              className="bg-[#0088CC] hover:bg-[#006DA6] text-white"
              data-testid="new-vessel-btn"
            >
              <Plus className="h-4 w-4 mr-2" /> New Vessel
            </Button>
          )}
        </div>
      </div>

      {/* Search / filter / favourites toolbar.

          Layout: the search field sits on its OWN row at the top, then a
          second row holds the type chips, the material chips and the
          favourites toggle. Stacking search above the chips gives it
          full breathing room (the previous side-by-side layout shrank
          the input to a tiny magnifying-glass icon on wider screens). */}
      <div className="space-y-3 mb-5">
        <div className="relative w-full sm:max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5B6B7C]" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search vessels…"
            className="pl-10 h-12 sm:h-10 rounded-full sm:rounded-md bg-[#F6F7F8] border-[#DDE5EC] text-[#011836] placeholder:text-[#5f6770]"
            data-testid="vessel-search"
          />
        </div>
        <div
          className="flex items-center gap-2 overflow-x-auto flex-nowrap sm:flex-wrap -mx-4 px-4 sm:mx-0 sm:px-0 pb-1 scrollbar-none"
          style={{ scrollbarWidth: "none" }}
        >
          {typeOptions.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTypeFilter(t)}
              className={`shrink-0 px-3.5 py-2 rounded-full text-xs font-display font-semibold uppercase tracking-wider border transition-colors active:scale-[0.97] ${
                typeFilter === t
                  ? "bg-[#0088CC] border-[#0088CC] text-white"
                  : "border-[#DDE5EC] text-[#5B6B7C] hover:text-[#011836] hover:border-[#C4D0DB]"
              }`}
              data-testid={`vessel-type-chip-${t}`}
            >
              {t}
            </button>
          ))}
          {materialOptions.length > 1 && (
            <>
              <span className="shrink-0 mx-1 h-4 w-px bg-[#E2E8F0]" aria-hidden="true" />
              {materialOptions.map((m) => (
                <button
                  key={`material-${m}`}
                  type="button"
                  onClick={() => setMaterialFilter(m)}
                  className={`shrink-0 px-3.5 py-2 rounded-full text-xs font-display font-semibold uppercase tracking-wider border transition-colors active:scale-[0.97] ${
                    materialFilter === m
                      ? "bg-[#0088CC] border-[#0088CC] text-white"
                      : "border-[#DDE5EC] text-[#5B6B7C] hover:text-[#011836] hover:border-[#C4D0DB]"
                  }`}
                  data-testid={`vessel-material-chip-${m}`}
                  title={m === "All" ? "Show all materials" : `Show only ${m} vessels`}
                >
                  {m}
                </button>
              ))}
            </>
          )}
          {!isClient && (
            <button
              type="button"
              onClick={() => setFavouritesOnly((f) => !f)}
              className={`shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-display font-semibold uppercase tracking-wider border transition-colors active:scale-[0.97] ${
                favouritesOnly
                  ? "bg-amber-500/20 border-amber-500 text-amber-700"
                  : "border-[#DDE5EC] text-[#5B6B7C] hover:text-[#011836] hover:border-[#C4D0DB]"
              }`}
              data-testid="favourites-only-toggle"
              title={favouritesOnly ? "Show all vessels" : "Show only favourites"}
            >
              <Star
                className={`h-3.5 w-3.5 ${favouritesOnly ? "fill-amber-300 text-amber-700" : ""}`}
              />
              Favourites
            </button>
          )}
        </div>
      </div>

      {compare.length > 0 && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-4 py-2.5 rounded-full bg-white/95 border border-[#0088CC]/40 shadow-xl backdrop-blur"
          data-testid="compare-bar"
        >
          <span className="text-xs text-[#5B6B7C] whitespace-nowrap">
            {compare.length} selected{compare.length < 2 ? " — pick one more" : ""}
          </span>
          <Button
            size="sm"
            disabled={compare.length < 2}
            onClick={() => navigate(`/vessels/compare?ids=${compare.join(",")}`)}
            className="bg-[#0088CC] hover:bg-[#006DA6] text-white h-8 rounded-full px-4"
            data-testid="compare-go-btn"
          >
            <ArrowLeftRight className="h-3.5 w-3.5 mr-1.5" /> Compare
          </Button>
          <button
            type="button"
            onClick={() => setCompare([])}
            className="text-xs text-[#5B6B7C] hover:text-[#011836]"
            data-testid="compare-clear-btn"
          >
            Clear
          </button>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
        {loading && <div className="text-[#5B6B7C] col-span-full text-sm">Loading…</div>}
        {!loading && filtered.length === 0 && (
          <div className="sos-surface rounded-2xl col-span-full p-12 text-center">
            <Ship className="h-10 w-10 text-[#B6C2CD] mx-auto mb-3" />
            <div className="text-sm text-[#5B6B7C]">
              {list.length === 0
                ? "No vessels yet"
                : "No vessels match your filters"}
            </div>
          </div>
        )}
        {filtered.map((v) => (
          <div
            key={v.id}
            className="sos-surface sos-surface-hover rounded-2xl overflow-hidden flex flex-col relative active:scale-[0.99] transition-transform"
            data-testid={`vessel-card-${v.id}`}
          >
            <Link
              to={`/vessels/${v.id}`}
              className="aspect-[16/10] bg-white border-b border-[#DDE5EC] relative block"
              data-testid={`open-vessel-${v.id}`}
            >
              {v.render_path ? (
                <img
                  src={imageSrc(v.render_path)}
                  alt={v.name}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Ship className="h-12 w-12 text-[#B6C2CD]" />
                </div>
              )}
                <div className="absolute top-2 left-2 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-black/70 backdrop-blur text-[#0088CC] border border-[#0088CC]/40">
                  {v.sub_category || v.vessel_type || "Vessel"}
                </div>
                {/* Draft badge — staff + admin see drafts; clients never do */}
                {!isClient && !v.is_live && (
                  <div
                    className="absolute bottom-2 left-2 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-amber-500/25 backdrop-blur text-amber-200 border border-amber-400/50 font-display font-semibold"
                    data-testid={`draft-badge-${v.id}`}
                    title="Not visible to clients — Go Live to publish"
                  >
                    Draft
                  </div>
                )}
              </Link>
            {!isClient && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleFavourite(v);
                }}
                className={`absolute top-2 right-2 z-10 h-8 w-8 rounded-full flex items-center justify-center backdrop-blur transition-colors ${
                  v.is_favourite
                    ? "bg-amber-500/30 text-amber-700 hover:bg-amber-500/40"
                    : "bg-black/60 text-white/70 hover:bg-black/80 hover:text-white"
                }`}
                data-testid={`fav-toggle-${v.id}`}
                title={v.is_favourite ? "Unfavourite" : "Favourite"}
              >
                <Star className={`h-4 w-4 ${v.is_favourite ? "fill-amber-300" : ""}`} />
              </button>
            )}
            {isClient && (() => {
              // Clients keep their own favourites list in `user.favourite_vessel_ids`.
              const isFav = (user?.favourite_vessel_ids || []).includes(v.id);
              return (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleFavourite(v);
                  }}
                  className={`absolute top-2 right-2 z-10 h-8 w-8 rounded-full flex items-center justify-center backdrop-blur transition-colors ${
                    isFav
                      ? "bg-amber-500/30 text-amber-700 hover:bg-amber-500/40"
                      : "bg-black/60 text-white/70 hover:bg-black/80 hover:text-white"
                  }`}
                  data-testid={`fav-toggle-${v.id}`}
                  title={isFav ? "Remove from favourites" : "Save as favourite"}
                >
                  <Star className={`h-4 w-4 ${isFav ? "fill-amber-300" : ""}`} />
                </button>
              );
            })()}
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleCompare(v.id);
              }}
              className={`absolute top-12 right-2 z-10 h-8 px-2.5 rounded-full flex items-center gap-1.5 text-[10px] font-display font-semibold uppercase tracking-wider backdrop-blur transition-colors ${
                compare.includes(v.id)
                  ? "bg-[#0088CC] text-white hover:bg-[#006DA6]"
                  : "bg-black/60 text-white/70 hover:bg-black/80 hover:text-white"
              }`}
              data-testid={`compare-toggle-${v.id}`}
              title={compare.includes(v.id) ? "Remove from comparison" : "Compare this vessel"}
            >
              {compare.includes(v.id) ? (
                <Check className="h-3.5 w-3.5" />
              ) : (
                <ArrowLeftRight className="h-3.5 w-3.5" />
              )}
              Compare
            </button>
            <div className="p-4 flex-1 flex flex-col">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <Link to={`/vessels/${v.id}`} data-testid={`open-vessel-name-${v.id}`}>
                    <h3 className="font-display text-base font-semibold text-[#011836] truncate hover:text-[#0088CC] transition-colors">
                      {v.name}
                    </h3>
                  </Link>
                  <div className="text-[10px] uppercase tracking-wider text-[#5B6B7C] mt-0.5 truncate">
                    {v.vessel_type || v.sub_category || "—"}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  {canEdit && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleLive(v);
                      }}
                      className={`px-2 py-1 rounded text-[10px] font-display font-semibold uppercase tracking-wider transition-colors ${
                        v.is_live
                          ? "bg-[#0088CC]/15 text-[#0088CC] hover:bg-[#0088CC]/25 border border-[#0088CC]/40"
                          : "bg-amber-500/15 text-amber-700 hover:bg-amber-500/25 border border-amber-400/40"
                      }`}
                      data-testid={`live-toggle-${v.id}`}
                      title={v.is_live ? "Click to hide from clients" : "Click to publish to clients"}
                    >
                      {v.is_live ? "Live" : "Go Live"}
                    </button>
                  )}
                  {!isClient && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openHistory(v);
                      }}
                      className="p-1.5 rounded text-[#5B6B7C] hover:text-[#0088CC] hover:bg-[#E6F2FA]"
                      data-testid={`vessel-history-${v.id}`}
                      title="Change history"
                    >
                      <History className="h-3.5 w-3.5" />
                    </button>
                  )}
                  {isAdmin && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openSocial(v);
                      }}
                      className="p-1.5 rounded text-[#5B6B7C] hover:text-[#0088CC] hover:bg-[#E6F2FA]"
                      data-testid={`social-vessel-${v.id}`}
                      title="Generate social media post"
                    >
                      <Share2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <button
                    onClick={() => openEdit(v)}
                    className={`p-1.5 rounded text-[#5B6B7C] hover:text-[#011836] hover:bg-[#EDF1F5] ${isClient || !canEdit ? "hidden" : ""}`}
                    data-testid={`edit-vessel-${v.id}`}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => remove(v)}
                    className={`p-1.5 rounded text-[#5B6B7C] hover:text-[#EF4444] hover:bg-[#FDECEC] ${isClient || !canEdit ? "hidden" : ""}`}
                    data-testid={`delete-vessel-${v.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2 mt-3 text-xs" data-testid={`vessel-card-pp-${v.id}`}>
                {(() => {
                  const picks = highlightedParticulars(v);
                  if (picks.length === 0) {
                    return (
                      <div className="col-span-3 text-[#5B6B7C] italic">
                        No principal particulars yet
                      </div>
                    );
                  }
                  return picks.map((p, idx) => (
                    <div key={`${p.label}-${idx}`}>
                      <div className="text-[#5B6B7C] uppercase tracking-wider text-[10px] truncate" title={p.label}>
                        {p.label || "—"}
                      </div>
                      <div
                        className="text-[#011836] font-display font-semibold mt-0.5 truncate"
                        title={p.value}
                        data-testid={`vessel-card-pp-${v.id}-${idx}`}
                      >
                        {p.value || "—"}
                      </div>
                    </div>
                  ));
                })()}
              </div>

              <div className="mt-3 pt-3 border-t border-[#DDE5EC] flex items-end justify-between gap-2">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#5B6B7C]">From</div>
                  <div className="text-[#0088CC] font-display text-lg font-bold tabular-nums leading-none mt-1">
                    {v.price != null ? formatMoney(v.price, v.price_currency) : "—"}
                  </div>
                  {/* Clients get the "Request quote" CTA stacked directly
                      under the price so the enquiry action sits next to the
                      number it relates to. */}
                  {isClient && (
                    <button
                      type="button"
                      onClick={() => openEnquiry(v)}
                      className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-display font-semibold uppercase tracking-wider bg-[#0088CC] hover:bg-[#006DA6] text-white transition-colors"
                      data-testid={`request-quote-${v.id}`}
                    >
                      <Mail className="h-3.5 w-3.5" /> Request quote
                    </button>
                  )}
                </div>
                {/* "View flyer" sits in its original top-right slot for
                    everyone — clients now see it again so they can browse
                    the spec sheet before requesting a quote. */}
                <Link
                  to={`/vessels/${v.id}/flyer`}
                  className="text-[11px] uppercase tracking-wider text-[#0088CC] hover:text-[#011836] inline-flex items-center gap-1"
                  data-testid={`view-flyer-${v.id}`}
                >
                  View flyer <ExternalLink className="h-3 w-3" />
                </Link>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog
        open={open}
        onOpenChange={(o) => {
          setOpen(o);
          if (!o) setReturnToAudit(false);
        }}
      >
        <DialogContent
          className="bg-white border-[#DDE5EC] text-[#011836] max-w-3xl max-h-[92vh] overflow-y-auto"
          data-testid="vessel-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              <Ship className="inline-block h-4 w-4 mr-2 text-[#0088CC]" />
              {editing ? "Edit vessel" : "New vessel"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5">
            <AuditPanel
              audit={audit}
              collapsed={auditCollapsed}
              onToggleCollapse={() => setAuditCollapsed((c) => !c)}
            />
            {/* Visibility toggle — admin + staff editors. Sits at the top so
                it's obvious whether a vessel is live or still drafting. */}
            {canEdit && (
              <div
                className={`flex items-center justify-between gap-3 p-3 rounded border ${
                  form.is_live
                    ? "border-[#0088CC]/40 bg-[#0088CC]/[0.06]"
                    : "border-amber-400/30 bg-amber-500/[0.06]"
                }`}
                data-testid="vessel-live-toggle-section"
              >
                <div className="min-w-0">
                  <div className="font-display text-sm font-semibold text-[#011836]">
                    {form.is_live ? "Live — visible to clients" : "Draft — hidden from clients"}
                  </div>
                  <div className="text-xs text-[#5B6B7C] mt-0.5">
                    {form.is_live
                      ? "All users can see this vessel and use it on quotes/flyers."
                      : "Only staff and admins can see this. Tick Go Live when the vessel is ready."}
                  </div>
                </div>
                <label className="flex items-center gap-2 cursor-pointer shrink-0">
                  <span className="text-xs uppercase tracking-wider text-[#5B6B7C]">Go Live</span>
                  <input
                    type="checkbox"
                    checked={!!form.is_live}
                    onChange={(e) => setForm({ ...form, is_live: e.target.checked })}
                    className="h-5 w-5 rounded border-[#C4D0DB] bg-white text-[#0088CC] focus:ring-2 focus:ring-[#0088CC]/60 cursor-pointer"
                    data-testid="vessel-live-checkbox"
                  />
                </label>
              </div>
            )}

            {/* Core fields */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">Name</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Tomcat Multi 35"
                  className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
                  data-testid="vessel-name-input"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">Type</Label>
                <TypeCombobox
                  value={form.vessel_type || ""}
                  onChange={(v) => setForm({ ...form, vessel_type: v })}
                  suggestions={Array.from(
                    new Set([
                      ...VESSEL_TYPES,
                      ...list.map((x) => (x.vessel_type || "").trim()).filter(Boolean),
                    ])
                  ).sort((a, b) => a.localeCompare(b))}
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">
                  Sub Category
                </Label>
                <Input
                  value={form.sub_category}
                  onChange={(e) => setForm({ ...form, sub_category: e.target.value })}
                  placeholder="Heavy Works Vessel"
                  className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
                  data-testid="vessel-subcategory-input"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">Client</Label>
                <Select
                  value={form.client_id || "none"}
                  onValueChange={(v) =>
                    setForm({ ...form, client_id: v === "none" ? null : v })
                  }
                >
                  <SelectTrigger className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836]">
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#DDE5EC] text-[#011836]">
                    <SelectItem value="none">— None —</SelectItem>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.company_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">
                  Price (from)
                </Label>
                <Input
                  type="number"
                  step="1000"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  placeholder="5800000"
                  className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
                  data-testid="vessel-price-input"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">Currency</Label>
                <Select
                  value={form.price_currency}
                  onValueChange={(v) => setForm({ ...form, price_currency: v })}
                >
                  <SelectTrigger className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#DDE5EC] text-[#011836]">
                    {CURRENCIES.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">
                  Material
                </Label>
                <Input
                  value={form.material}
                  onChange={(e) => setForm({ ...form, material: e.target.value })}
                  placeholder="Aluminium / Steel / FRP"
                  className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
                />
              </div>
            </div>

            {/* Images */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <ImageUploadBox
                label="Hero / Render"
                hint="Showcase image — appears at the top of the flyer."
                path={form.render_path}
                uploading={uploadingKey === "render_path"}
                onPick={() => renderRef.current?.click()}
                onRemove={() => setForm({ ...form, render_path: null })}
                inputRef={renderRef}
                onChange={onRenderPick}
                testid="render"
              />
              <ImageUploadBox
                label="GA Drawing"
                hint="PDF or image — flyer uses page 1 preview."
                path={form.ga_preview_path || form.ga_path}
                originalPath={form.ga_path}
                uploading={uploadingKey === "ga_path"}
                onPick={() => gaRef.current?.click()}
                onRemove={() => setForm({ ...form, ga_path: null, ga_preview_path: null })}
                inputRef={gaRef}
                onChange={onGaPick}
                accept="application/pdf,image/png,image/jpeg,image/webp"
                testid="ga"
              />
              {/* Regenerate preview — visible whenever the GA is a PDF on a
                  saved vessel. Lets users self-heal any broken / stale preview
                  (e.g. uploads from before PyMuPDF was wired in) with one click. */}
              {editing?.id &&
                form.ga_path &&
                /\.pdf(\?|$)/i.test(form.ga_path) && (
                  <button
                    type="button"
                    onClick={regenerateGaPreview}
                    disabled={regenerating}
                    title="Re-render page 1 of the GA PDF to a fresh PNG preview."
                    className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded border border-[#0088CC]/40 text-[#0088CC] hover:bg-[#0088CC]/10 disabled:opacity-50 text-[11px] font-display font-semibold uppercase tracking-wider"
                    data-testid="regenerate-ga-preview"
                  >
                    <RefreshCcw className={`h-3.5 w-3.5 ${regenerating ? "animate-spin" : ""}`} />
                    {regenerating ? "Regenerating…" : "Regenerate GA Preview"}
                  </button>
              )}
              {/* AI hero generation — only meaningful once a GA is uploaded.
                  Sits in the same column as the Hero/Render slot so the
                  action and its output are visually adjacent. */}
              {(form.ga_path || form.ga_preview_path) && (
                <div className="mt-2 rounded-lg border border-[#0088CC]/25 bg-[#0088CC]/[0.04] p-3 space-y-2.5">
                  <div className="flex items-start gap-2.5">
                    <Sparkles className="h-4 w-4 text-[#0088CC] mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-[12px] font-display font-semibold text-[#011836] uppercase tracking-wider">
                        {form.render_path ? "Refine the AI hero" : "Generate AI hero from GA"}
                      </div>
                      <div className="text-[11px] text-[#5B6B7C] leading-snug mt-0.5">
                        {form.render_path
                          ? "Type what needs to change and click Refine. Leave blank to roll a fresh variation."
                          : "Click Generate to produce a brand-styled render from the uploaded GA."}
                      </div>
                    </div>
                  </div>
                  <textarea
                    rows={2}
                    value={heroFeedback}
                    onChange={(e) => setHeroFeedback(e.target.value)}
                    placeholder={
                      form.render_path
                        ? "e.g. 'Make the hull darker. Add a red stripe along the waterline. Move the crane further aft.'"
                        : "Optional style notes — e.g. 'twin engines, aft crane, yellow accents.'"
                    }
                    disabled={heroGenerating}
                    className="w-full rounded border border-[#DDE5EC] bg-white text-[#011836] text-[12px] px-2.5 py-2 placeholder:text-[#475569]/70 focus:outline-none focus:ring-1 focus:ring-[#0088CC]/60 resize-none disabled:opacity-50"
                    data-testid="hero-feedback-input"
                  />
                  <button
                    type="button"
                    onClick={generateHeroFromGa}
                    disabled={heroGenerating}
                    title="Generate a brand-styled hero render from the GA using AI (Gemini)."
                    className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded bg-gradient-to-r from-[#0088CC] to-[#0088CC] text-white hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed text-[11px] font-display font-semibold uppercase tracking-wider shadow-sm shadow-[#0088CC]/30"
                    data-testid="generate-hero-from-ga"
                  >
                    <Sparkles className={`h-3.5 w-3.5 ${heroGenerating ? "animate-pulse" : ""}`} />
                    {heroGenerating
                      ? "Rendering… (10-30s)"
                      : form.render_path
                        ? (heroFeedback.trim() ? "Refine Hero" : "Regenerate Hero")
                        : "Generate Hero from GA"}
                  </button>
                </div>
              )}
              {/* GA rotation control — applied to the GA shown on the flyer */}
              {form.ga_path && (
                <div className="mt-2 flex items-center gap-2 text-[11px] text-[#5B6B7C]">
                  <span className="uppercase tracking-wider">GA rotation:</span>
                  {[0, 90, 180, 270].map((deg) => (
                    <button
                      key={deg}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, ga_rotation: deg }))}
                      className={`px-2 py-1 rounded border text-[11px] font-display font-semibold ${
                        (form.ga_rotation || 0) === deg
                          ? "border-[#0088CC] text-[#0088CC] bg-[#0088CC]/10"
                          : "border-[#DDE5EC] text-[#5B6B7C] hover:text-[#011836]"
                      }`}
                      data-testid={`ga-rotation-${deg}`}
                    >
                      {deg}°
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* AI generate from images + confirmed PP. */}
            <div className="rounded-md border border-[#0088CC]/30 bg-[#E6F2FA]/30 p-4 space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex items-start gap-3 flex-1">
                  <Sparkles className="h-5 w-5 text-[#0088CC] shrink-0 mt-0.5" />
                  <div>
                    <div className="font-display text-sm font-semibold text-[#011836]">
                      Generate description from GA, render & confirmed PP
                    </div>
                    <div className="text-xs text-[#5B6B7C] mt-0.5">
                      Enter Principal Particulars first, then the AI quotes
                      them verbatim while writing the description and 5 key
                      features. (Claude Sonnet 4.5 · vision)
                    </div>
                    {confirmedPpCount < 3 && (
                      <div
                        className="mt-1.5 text-xs text-amber-700/90"
                        data-testid="ai-describe-pp-hint"
                      >
                        {confirmedPpCount}/3 Principal Particulars confirmed — add
                        {" "}{3 - confirmedPpCount} more before generating.
                      </div>
                    )}
                  </div>
                </div>
                <Button
                  type="button"
                  onClick={runAiDescribe}
                  disabled={aiDescribeDisabled}
                  title={aiDescribeBlockReason || ""}
                  className="bg-[#0088CC] hover:bg-[#006DA6] text-white disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="vessel-ai-describe-btn"
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  {aiLoading ? "Reading images…" : "Generate"}
                </Button>
                {/* Shuffle — re-roll description + features only. Hidden until
                    there's something to re-roll (otherwise it's redundant
                    with Generate). Same PP gating as the main describe call. */}
                {(form.description || (form.key_features || []).some((kf) => kf.title || kf.description)) && (
                  <Button
                    type="button"
                    onClick={runAiShuffle}
                    disabled={aiDescribeDisabled}
                    title={aiDescribeBlockReason || "Generate a new variant — your particulars stay locked"}
                    variant="outline"
                    className="border-[#0088CC]/40 text-[#0088CC] hover:bg-[#0088CC]/10 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="vessel-ai-shuffle-btn"
                  >
                    <Shuffle className={`h-4 w-4 mr-2 ${aiLoading ? "animate-pulse" : ""}`} />
                    Shuffle
                  </Button>
                )}
              </div>

              {/* Refine — only enabled once there's a description / features to
                  edit. Touches description + key features ONLY; principal
                  particulars and the manual numeric fields below are
                  preserved as-is. */}
              <div className="pt-3 border-t border-[#0088CC]/15">
                <Label className="text-[10px] uppercase tracking-[0.18em] text-[#5B6B7C]">
                  Not quite right? Tell the AI what to change
                </Label>
                <div className="mt-1.5 flex flex-col sm:flex-row gap-2">
                  <Textarea
                    value={refineFeedback}
                    onChange={(e) => setRefineFeedback(e.target.value)}
                    rows={2}
                    placeholder='e.g. "Call it a crew transfer vessel, not a workboat. Mention the heated cabin."'
                    className="flex-1 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC] text-sm"
                    data-testid="vessel-ai-refine-input"
                    disabled={refineLoading}
                  />
                  <Button
                    type="button"
                    onClick={runAiRefine}
                    disabled={
                      refineLoading ||
                      !refineFeedback.trim() ||
                      (!form.description && (!form.key_features || form.key_features.length === 0))
                    }
                    className="bg-[#0088CC]/15 hover:bg-[#0088CC]/25 border border-[#0088CC]/40 text-[#0088CC] disabled:opacity-50 sm:self-stretch"
                    data-testid="vessel-ai-refine-btn"
                    title="Rewrites the description and key features only — particulars and dimensions are preserved"
                  >
                    {refineLoading ? "Refining…" : "Refine"}
                  </Button>
                </div>
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">Tagline</Label>
              <Input
                value={form.tagline}
                onChange={(e) => setForm({ ...form, tagline: e.target.value })}
                placeholder="Built for performance. Engineered to endure."
                className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
                data-testid="vessel-tagline-input"
              />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">
                Description
              </Label>
              <Textarea
                value={form.description}
                rows={4}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="A purpose-built heavy works vessel designed for…"
                className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
              />
            </div>

            {/* Key features */}
            <div className="border-t border-[#DDE5EC] pt-4">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs uppercase tracking-[0.25em] text-[#0088CC]">
                  Key Features
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={addFeature}
                  className="border-[#C4D0DB] text-[#011836] hover:bg-[#EDF1F5] hover:text-[#011836]"
                >
                  <Plus className="h-3.5 w-3.5 mr-1.5" /> Add
                </Button>
              </div>
              <div className="space-y-2">
                {(() => {
                  // Compute auto-pick icons once per render so each picker
                  // can show the auto choice as its fallback preview.
                  const autoIcons = pickFeatureIcons(form.key_features || []);
                  return form.key_features.map((kf, i) => (
                    <div
                      key={i}
                      className="grid grid-cols-12 gap-2 items-start rounded-md border border-[#DDE5EC] bg-white p-3"
                      data-testid={`feature-row-${i}`}
                    >
                      <div className="col-span-1 flex justify-center pt-1">
                        <FeatureIconPicker
                          feature={kf}
                          fallbackIcon={autoIcons[i]}
                          onChange={(next) => updateFeature(i, { icon_key: next.icon_key })}
                        />
                      </div>
                      <div className="col-span-3">
                        <Input
                          value={kf.title}
                          onChange={(e) => updateFeature(i, { title: e.target.value })}
                          placeholder="Title"
                          className="bg-white border-[#DDE5EC] text-[#011836] h-9 focus-visible:ring-[#0088CC]"
                          data-testid={`feature-title-${i}`}
                        />
                      </div>
                      <div className="col-span-7">
                        <Input
                          value={kf.description}
                          onChange={(e) => updateFeature(i, { description: e.target.value })}
                          placeholder="Short description"
                          className="bg-white border-[#DDE5EC] text-[#011836] h-9 focus-visible:ring-[#0088CC]"
                          data-testid={`feature-description-${i}`}
                        />
                      </div>
                      <div className="col-span-1 flex justify-end">
                        <button
                          type="button"
                          onClick={() => removeFeature(i)}
                          className="p-1.5 rounded text-[#5B6B7C] hover:text-[#EF4444] hover:bg-[#FDECEC]"
                          aria-label="Remove feature"
                          data-testid={`remove-feature-${i}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>

            {/* Principal particulars */}
            <div className="border-t border-[#DDE5EC] pt-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-xs uppercase tracking-[0.25em] text-[#0088CC] flex items-center gap-2 flex-wrap">
                    Principal Particulars
                    {(() => {
                      // Live ticked count — the flyer template fits exactly 11
                      // DATA rows. Section headings render as free dividers
                      // and don't consume slots, so they're excluded here
                      // (matches the backend audit rule).
                      const PP_CAP = 11;
                      const dataRows = (form.principal_particulars || []).filter(
                        (sp) => (sp.kind || "row") !== "heading"
                      );
                      const enabledCount = dataRows.filter(
                        (sp) => sp.enabled !== false
                      ).length;
                      const over = enabledCount > PP_CAP;
                      return (
                        <span
                          className={`text-[10px] tracking-normal font-display font-semibold px-1.5 py-0.5 rounded border ${
                            over
                              ? "bg-[#FDECEC] text-[#EF4444] border-[#EF4444]/40"
                              : "bg-white text-[#5B6B7C] border-[#C4D0DB]"
                          }`}
                          data-testid="pp-tick-count"
                          title={
                            over
                              ? `Flyer template fits ${PP_CAP} data rows — untick ${enabledCount - PP_CAP}`
                              : `Flyer template fits ${PP_CAP} data rows (headings are free)`
                          }
                        >
                          {enabledCount} / {PP_CAP} ticked
                        </span>
                      );
                    })()}
                    {(() => {
                      // Live count of rows flagged "Show on card" — the
                      // vessel card has room for exactly 3. Mirrors the
                      // 11-row flyer cap badge so the admin sees both
                      // constraints side-by-side.
                      const CARD_CAP = 3;
                      const highlightedCount = (form.principal_particulars || []).filter(
                        (sp) => (sp.kind || "row") !== "heading"
                          && sp.is_highlighted === true
                      ).length;
                      const over = highlightedCount > CARD_CAP;
                      return (
                        <span
                          className={`text-[10px] tracking-normal font-display font-semibold px-1.5 py-0.5 rounded border ${
                            over
                              ? "bg-[#FDECEC] text-[#EF4444] border-[#EF4444]/40"
                              : "bg-white text-[#5B6B7C] border-[#C4D0DB]"
                          }`}
                          data-testid="pp-card-count"
                          title={
                            over
                              ? `Vessel card fits ${CARD_CAP} — untick ${highlightedCount - CARD_CAP} 'Show on card' row(s)`
                              : `Vessel card fits ${CARD_CAP}`
                          }
                        >
                          {highlightedCount} / {CARD_CAP} on card
                        </span>
                      );
                    })()}
                  </div>
                  <div className="text-[10px] text-[#5B6B7C] mt-1">
                    Tick rows to include on the flyer (max 11 data rows; headings are free). Tick &ldquo;Show on card&rdquo; on up to 3 rows to feature them on the catalogue card.
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {(form.ga_path || form.ga_preview_path) && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setCompareOpen(true)}
                      className="border-[#0088CC]/50 text-[#0088CC] hover:bg-[#E6F2FA] hover:text-[#011836]"
                      data-testid="compare-with-ga-btn"
                      title="Open a side-by-side review of these values against the GA drawing"
                    >
                      <FileImage className="h-3.5 w-3.5 mr-1.5" /> Compare with GA
                    </Button>
                  )}
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={addHeading}
                    className="border-[#C4D0DB] text-[#011836] hover:bg-[#EDF1F5] hover:text-[#011836]"
                    data-testid="add-heading-btn"
                    title="Insert a section heading (like 'MACHINERY' on a GA) — manually managed only"
                  >
                    <Plus className="h-3.5 w-3.5 mr-1.5" /> Heading
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={addSpec}
                    className="border-[#C4D0DB] text-[#011836] hover:bg-[#EDF1F5] hover:text-[#011836]"
                    data-testid="add-particular-btn"
                  >
                    <Plus className="h-3.5 w-3.5 mr-1.5" /> Add row
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                {form.principal_particulars.length > 0 && (
                  <div
                    className="hidden md:grid grid-cols-12 gap-2 items-center px-3 pb-1 text-[10px] uppercase tracking-wider text-[#5B6B7C]"
                    aria-hidden="true"
                  >
                    <div className="col-span-1 text-center">Drag</div>
                    <div className="col-span-1 text-center" title="Include this row on the flyer">Flyer</div>
                    <div className="col-span-1 text-center" title="Show this row on the vessel card (max 3)">Card</div>
                    <div className="col-span-3">Label</div>
                    <div className="col-span-5">Value</div>
                    <div className="col-span-1" />
                  </div>
                )}
                {form.principal_particulars.length === 0 && (
                  <div className="text-xs text-[#5B6B7C]">
                    Add at least 3 Principal Particulars (LOA, Beam, Engines, etc.) — the AI describe job and the vessel card both rely on them.
                  </div>
                )}
                {form.principal_particulars.map((sp, i) => {
                  const isHeading = (sp.kind || "row") === "heading";
                  const isDragging = ppReorder.draggingIndex === i;
                  const isOver = ppReorder.overIndex === i && ppReorder.draggingIndex !== null && ppReorder.draggingIndex !== i;

                  // Section heading rows — full-width label only, no
                  // Flyer/Card checkboxes, no value column, no audit cap.
                  // Visually distinct so admins instantly see they're a
                  // divider between groups (e.g. MACHINERY / TANK CAPACITY).
                  if (isHeading) {
                    // Headings inherit the same Flyer-show toggle that data
                    // rows use — `enabled !== false` means it appears on
                    // the produced flyer. Defaults to true; admins untick
                    // to drop a section divider on a per-vessel basis
                    // without deleting the row.
                    const headingEnabled = sp.enabled !== false;
                    return (
                      <div
                        key={i}
                        {...ppReorder.rowProps(i)}
                        className={`grid grid-cols-12 gap-2 items-center rounded-md border border-[#0088CC]/30 bg-[#E6F2FA]/40 p-3 transition-colors ${
                          headingEnabled ? "" : "opacity-50"
                        } ${isDragging ? "opacity-40" : ""} ${isOver ? "ring-2 ring-[#0088CC]/60 border-[#0088CC]/40" : ""}`}
                        data-testid={`particular-row-${i}`}
                        data-kind="heading"
                      >
                        <div className="col-span-1 flex justify-center">
                          <button
                            type="button"
                            {...ppReorder.handleProps(i)}
                            className="p-1 text-[#5B6B7C] hover:text-[#0088CC] cursor-grab active:cursor-grabbing"
                            title="Drag to reorder"
                            data-testid={`particular-drag-${i}`}
                          >
                            <GripVertical className="h-4 w-4" />
                          </button>
                        </div>
                        <div className="col-span-1 flex justify-center">
                          <Checkbox
                            checked={headingEnabled}
                            onCheckedChange={(v) => updateSpec(i, { enabled: !!v })}
                            className="border-[#C4D0DB] data-[state=checked]:bg-[#0088CC] data-[state=checked]:border-[#0088CC]"
                            data-testid={`particular-checkbox-${i}`}
                            aria-label={`Show ${sp.label || "heading"} on the flyer`}
                            title="Show this section heading on the flyer"
                          />
                        </div>
                        <div className="col-span-1 flex items-center">
                          <span
                            className="text-[10px] uppercase tracking-[0.25em] text-[#0088CC] font-display font-semibold px-2 py-0.5 rounded border border-[#0088CC]/40 bg-white"
                            data-testid={`particular-heading-badge-${i}`}
                          >
                            Heading
                          </span>
                        </div>
                        <div className="col-span-8">
                          <Input
                            value={sp.label}
                            onChange={(e) => updateSpec(i, { label: e.target.value })}
                            placeholder="LOAD CAPACITY"
                            className="bg-white border-[#DDE5EC] text-[#011836] font-display font-bold uppercase tracking-wider h-9 focus-visible:ring-[#0088CC]"
                            data-testid={`particular-heading-label-${i}`}
                          />
                        </div>
                        <div className="col-span-1 flex justify-end">
                          <button
                            type="button"
                            onClick={() => removeSpec(i)}
                            className="p-1.5 rounded text-[#5B6B7C] hover:text-[#EF4444] hover:bg-[#FDECEC]"
                            aria-label="Remove heading"
                            data-testid={`remove-particular-${i}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    );
                  }

                  const enabled = sp.enabled !== false;
                  const highlighted = sp.is_highlighted === true;
                  const highlightedCount = (form.principal_particulars || []).filter(
                    (x) => (x.kind || "row") !== "heading"
                      && x.is_highlighted === true
                  ).length;
                  // Disable the "Show on card" checkbox for unticked rows
                  // (highlighting a row you've hidden makes no sense) and
                  // once the 3-row cap is hit (admin must untick another
                  // row first). Already-highlighted rows stay clickable so
                  // they can be removed even at the cap.
                  const highlightDisabled =
                    !enabled || (!highlighted && highlightedCount >= 3);
                  return (
                    <div
                      key={i}
                      {...ppReorder.rowProps(i)}
                      className={`grid grid-cols-12 gap-2 items-center rounded-md border p-3 transition-colors ${
                        enabled
                          ? "border-[#DDE5EC] bg-white"
                          : "border-[#DDE5EC] bg-white/40 opacity-60"
                      } ${highlighted ? "ring-1 ring-[#0088CC]/40" : ""} ${isDragging ? "opacity-40" : ""} ${
                        isOver ? "ring-2 ring-[#0088CC]/60 border-[#0088CC]/40" : ""
                      }`}
                      data-testid={`particular-row-${i}`}
                      data-kind="row"
                    >
                      <div className="col-span-1 flex justify-center">
                        <button
                          type="button"
                          {...ppReorder.handleProps(i)}
                          className="p-1 text-[#5B6B7C] hover:text-[#0088CC] cursor-grab active:cursor-grabbing"
                          title="Drag to reorder"
                          data-testid={`particular-drag-${i}`}
                        >
                          <GripVertical className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="col-span-1 flex justify-center">
                        <Checkbox
                          checked={enabled}
                          onCheckedChange={(v) => {
                            // Auto-clear highlight when an admin hides a
                            // row — keeps DB state self-consistent so a
                            // hidden row never sneaks onto the card.
                            const patch = { enabled: !!v };
                            if (!v) patch.is_highlighted = false;
                            updateSpec(i, patch);
                          }}
                          className="border-[#C4D0DB] data-[state=checked]:bg-[#0088CC] data-[state=checked]:border-[#0088CC]"
                          data-testid={`particular-checkbox-${i}`}
                          aria-label={`Include ${sp.label || "row"} on the flyer`}
                        />
                      </div>
                      <div className="col-span-1 flex justify-center">
                        <Checkbox
                          checked={highlighted}
                          disabled={highlightDisabled}
                          onCheckedChange={(v) => updateSpec(i, { is_highlighted: !!v })}
                          className="border-[#C4D0DB] data-[state=checked]:bg-[#0088CC] data-[state=checked]:border-[#0088CC] disabled:opacity-40 disabled:cursor-not-allowed"
                          data-testid={`particular-highlight-${i}`}
                          aria-label={`Show ${sp.label || "row"} on the vessel card`}
                          title={
                            !enabled
                              ? "Tick the row first to feature it"
                              : !highlighted && highlightedCount >= 3
                                ? "Max 3 rows can be shown on the card — untick another first"
                                : "Show this row on the vessel catalogue card"
                          }
                        />
                      </div>
                      <div className="col-span-3">
                        <Input
                          value={sp.label}
                          onChange={(e) => updateSpec(i, { label: e.target.value })}
                          placeholder="Length Overall"
                          className="bg-white border-[#DDE5EC] text-[#011836] h-9 focus-visible:ring-[#0088CC]"
                          data-testid={`particular-label-${i}`}
                        />
                      </div>
                      <div className="col-span-5">
                        <Input
                          value={sp.value}
                          onChange={(e) => updateSpec(i, { value: e.target.value })}
                          placeholder="35.00 m"
                          className="bg-white border-[#DDE5EC] text-[#011836] h-9 focus-visible:ring-[#0088CC]"
                          data-testid={`particular-value-${i}`}
                        />
                      </div>
                      <div className="col-span-1 flex justify-end">
                        <button
                          type="button"
                          onClick={() => removeSpec(i)}
                          className="p-1.5 rounded text-[#5B6B7C] hover:text-[#EF4444] hover:bg-[#FDECEC]"
                          aria-label="Remove particular"
                          data-testid={`remove-particular-${i}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">Notes</Label>
              <Textarea
                value={form.notes}
                rows={2}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              className="border-[#C4D0DB] text-[#011836] hover:bg-[#EDF1F5]"
            >
              Cancel
            </Button>
            <Button
              onClick={save}
              className="bg-[#0088CC] hover:bg-[#006DA6] text-white"
              data-testid="save-vessel-btn"
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CompareWithGA
        open={compareOpen}
        onClose={() => setCompareOpen(false)}
        form={form}
        setForm={setForm}
        vesselName={form?.name}
      />

      {/* Vessel change history (staff + admin) */}
      <Dialog
        open={historyModal.open}
        onOpenChange={(o) =>
          o ? null : setHistoryModal({ open: false, vessel: null, entries: [], loading: false })
        }
      >
        <DialogContent
          className="sos-surface text-[#011836] max-w-xl"
          data-testid="vessel-history-modal"
        >
          <DialogHeader>
            <DialogTitle className="font-display tracking-wide flex items-center gap-2">
              <History className="h-4 w-4 text-[#0088CC]" />
              History — {historyModal.vessel?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto pr-1 space-y-3" data-testid="vessel-history-list">
            {historyModal.loading && (
              <div className="text-sm text-[#5B6B7C] py-6 text-center">Loading history…</div>
            )}
            {!historyModal.loading && historyModal.entries.length === 0 && (
              <div className="text-sm text-[#5B6B7C] py-6 text-center">
                No history yet — changes made from now on will be recorded here.
              </div>
            )}
            {historyModal.entries.map((h) => (
              <div
                key={h.id}
                className="rounded border border-[#DDE5EC] bg-white/60 p-3"
                data-testid={`vessel-history-entry-${h.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-display text-sm font-semibold text-[#011836]">
                      {h.action}
                    </div>
                    <div className="text-xs text-[#5B6B7C] mt-0.5">
                      {h.by?.name || "Unknown"}
                      {h.by?.role ? ` · ${h.by.role}` : ""}
                    </div>
                  </div>
                  <div className="text-[11px] text-[#5B6B7C] shrink-0 text-right">
                    {h.at
                      ? new Date(h.at).toLocaleString("en-AU", {
                          timeZone: "Australia/Sydney",
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : ""}
                  </div>
                </div>
                {h.changes?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {h.changes.map((c, i) => {
                      const isObj = typeof c === "object" && c !== null;
                      const summary = isObj ? c.summary : c;
                      const hasDetail = isObj && ((c.from ?? "") !== "" || (c.to ?? "") !== "");
                      if (!hasDetail) {
                        return (
                          <span
                            key={i}
                            className="px-2 py-0.5 rounded text-[11px] bg-[#0088CC]/10 text-[#0088CC] border border-[#0088CC]/30 break-all"
                          >
                            {summary}
                          </span>
                        );
                      }
                      return (
                        <details
                          key={i}
                          className="w-full rounded border border-[#0088CC]/30 bg-[#0088CC]/5"
                        >
                          <summary className="cursor-pointer px-2 py-1 text-[11px] text-[#0088CC] select-none">
                            {summary} — click to view before/after
                          </summary>
                          <div className="px-2 pb-2 space-y-1.5">
                            {(c.from ?? "") !== "" && (
                              <div>
                                <div className="text-[10px] uppercase tracking-wider text-[#5B6B7C]">Before</div>
                                <div className="text-xs text-[#33475C] whitespace-pre-wrap bg-white/80 rounded p-1.5 border border-[#DDE5EC]">
                                  {c.from}
                                </div>
                              </div>
                            )}
                            {(c.to ?? "") !== "" && (
                              <div>
                                <div className="text-[10px] uppercase tracking-wider text-[#5B6B7C]">After</div>
                                <div className="text-xs text-[#33475C] whitespace-pre-wrap bg-white/80 rounded p-1.5 border border-[#DDE5EC]">
                                  {c.to}
                                </div>
                              </div>
                            )}
                          </div>
                        </details>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Social media post generator (admin-only) */}
      <Dialog
        open={socialModal.open}
        onOpenChange={(o) => (o ? null : closeSocial())}
      >
        <DialogContent
          className="sos-surface text-[#011836] max-w-2xl"
          data-testid="vessel-social-modal"
        >
          <DialogHeader>
            <DialogTitle className="font-display tracking-wide">
              Social post — {socialModal.vessel?.name}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">Platform</Label>
              <div className="mt-1.5 flex flex-wrap gap-2">
                {["linkedin", "facebook", "instagram", "twitter"].map((p) => (
                  <button
                    type="button"
                    key={p}
                    onClick={() => setSocialModal((m) => ({ ...m, platform: p }))}
                    className={`px-3 py-1.5 rounded-full text-xs font-display font-semibold uppercase tracking-wider border transition-colors ${
                      socialModal.platform === p
                        ? "bg-[#0088CC] border-[#0088CC] text-white"
                        : "border-[#DDE5EC] text-[#5B6B7C] hover:text-[#011836] hover:border-[#C4D0DB]"
                    }`}
                    data-testid={`social-platform-${p}`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">
                Optional tweak (tone, angle, audience)
              </Label>
              <Textarea
                value={socialModal.feedback}
                onChange={(e) => setSocialModal((m) => ({ ...m, feedback: e.target.value }))}
                rows={2}
                placeholder='e.g. "Lead with the heated cabin. Aim at aquaculture operators."'
                className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC] text-sm"
                data-testid="social-feedback-input"
                disabled={socialModal.loading}
              />
            </div>

            <div className="flex justify-end">
              <Button
                type="button"
                onClick={() => generateSocial()}
                disabled={socialModal.loading}
                className="bg-[#0088CC] hover:bg-[#006DA6] text-white disabled:opacity-50"
                data-testid="social-generate-btn"
              >
                <Sparkles className="h-4 w-4 mr-2" />
                {socialModal.loading
                  ? "Writing…"
                  : socialModal.text
                  ? "Regenerate"
                  : "Generate"}
              </Button>
            </div>

            {(socialModal.text || socialModal.hashtags.length > 0) && (
              <div className="rounded-md border border-[#DDE5EC] bg-white p-4">
                <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">
                  Generated post
                </Label>
                <Textarea
                  value={socialModal.text}
                  onChange={(e) =>
                    setSocialModal((m) => ({ ...m, text: e.target.value }))
                  }
                  rows={8}
                  className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC] text-sm font-mono"
                  data-testid="social-text-output"
                />
                {socialModal.hashtags.length > 0 && (
                  <div className="mt-3">
                    <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">
                      Hashtags ({socialModal.hashtags.length})
                    </Label>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {socialModal.hashtags.map((h, i) => (
                        <span
                          key={i}
                          className="text-[11px] font-mono px-2 py-1 rounded bg-[#0088CC]/10 text-[#0088CC] border border-[#0088CC]/30"
                          data-testid={`social-hashtag-${i}`}
                        >
                          {h}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                <div className="mt-3 flex gap-2 justify-end">
                  <Button
                    type="button"
                    onClick={() => generateSocial({ platform: socialModal.platform, feedback: socialModal.feedback })}
                    disabled={socialModal.loading}
                    variant="outline"
                    className="border-[#DDE5EC] text-[#5B6B7C] hover:text-[#011836] hover:border-[#C4D0DB]"
                    data-testid="social-regenerate-btn"
                  >
                    <RefreshCcw className="h-3.5 w-3.5 mr-1.5" />
                    Try again
                  </Button>
                  <Button
                    type="button"
                    onClick={copySocial}
                    className="bg-[#0088CC] hover:bg-[#006DA6] text-white"
                    data-testid="social-copy-btn"
                  >
                    <Copy className="h-3.5 w-3.5 mr-1.5" />
                    Copy post + hashtags
                  </Button>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={closeSocial}
              className="border-[#DDE5EC] text-[#5B6B7C] hover:text-[#011836] hover:border-[#C4D0DB]"
              data-testid="social-close-btn"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Client enquiry modal — prefilled with vessel + the client's own
          profile so the message field is the only thing they need to type. */}
      <Dialog open={enquiryModal.open} onOpenChange={(o) => (!o ? closeEnquiry() : null)}>
        <DialogContent
          className="bg-white border-[#DDE5EC] text-[#011836]"
          data-testid="enquiry-modal"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              Request a quote
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-sm text-[#5B6B7C]">
              Send the Southern Ocean Solutions sales team a note about
              <span className="text-[#011836] font-semibold mx-1">
                {enquiryModal.vessel?.name}
              </span>
              — we&apos;ll reply to your registered email and phone.
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <div className="rounded border border-[#DDE5EC] bg-white p-3">
                <div className="uppercase tracking-wider text-[10px] text-[#5B6B7C]">From</div>
                <div className="mt-1 text-[#011836]">{user?.name}</div>
                {user?.company && (
                  <div className="text-[#5B6B7C]">{user.company}</div>
                )}
              </div>
              <div className="rounded border border-[#DDE5EC] bg-white p-3">
                <div className="uppercase tracking-wider text-[10px] text-[#5B6B7C]">Contact</div>
                <div className="mt-1 text-[#011836]">{user?.email}</div>
                {user?.phone && (
                  <div className="text-[#5B6B7C]">{user.phone}</div>
                )}
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">
                Message <span className="normal-case text-[#64748B]">(optional)</span>
              </Label>
              <Textarea
                value={enquiryModal.message}
                onChange={(e) =>
                  setEnquiryModal((m) => ({ ...m, message: e.target.value }))
                }
                placeholder="Anything specific you'd like us to address — timeline, customisations, delivery location, budget…"
                className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] min-h-[140px]"
                data-testid="enquiry-message-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={closeEnquiry}
              disabled={enquiryModal.submitting}
              className="border-[#DDE5EC] text-[#5B6B7C] hover:text-[#011836] hover:border-[#C4D0DB]"
              data-testid="enquiry-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={submitEnquiry}
              disabled={enquiryModal.submitting}
              className="bg-[#0088CC] hover:bg-[#006DA6] text-white"
              data-testid="enquiry-submit-btn"
            >
              {enquiryModal.submitting ? "Sending…" : "Send enquiry"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ImageUploadBox({
  label,
  hint,
  path,
  originalPath,
  uploading,
  onPick,
  onRemove,
  inputRef,
  onChange,
  accept = "image/png,image/jpeg,image/webp",
  testid,
}) {
  // The GA originals are typically PDFs while ``path`` points at the
  // rendered preview PNG. Admin downloads should pull the ORIGINAL file
  // the way they uploaded it — fall back to the preview path when no
  // original is tracked (e.g. plain image uploads).
  const downloadPath = originalPath || path;
  const [downloading, setDownloading] = useState(false);
  const handleDownload = async () => {
    if (!downloadPath || downloading) return;
    setDownloading(true);
    const tid = toast.loading("Preparing download…");
    try {
      await downloadAuthedFile(downloadPath);
      toast.success("Download started", { id: tid });
    } catch (e) {
      toast.error(e?.message || "Could not download file", { id: tid });
    } finally {
      setDownloading(false);
    }
  };
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">{label}</Label>
      <div className="mt-1.5 rounded-md border border-[#DDE5EC] bg-white overflow-hidden">
        <div className="aspect-[16/10] relative flex items-center justify-center">
          {path ? (
            <img
              src={imageSrc(path)}
              alt={label}
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : (
            <FileImage className="h-10 w-10 text-[#B6C2CD]" />
          )}
          {originalPath && originalPath !== path && (
            <div className="absolute top-2 right-2 text-[9px] uppercase tracking-wider px-2 py-0.5 rounded bg-black/70 text-[#0088CC] border border-[#0088CC]/40">
              PDF
            </div>
          )}
        </div>
        <div className="p-3 flex items-center gap-2 border-t border-[#DDE5EC]">
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            className="hidden"
            onChange={onChange}
            data-testid={`vessel-${testid}-input`}
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={onPick}
            disabled={uploading}
            className="border-[#C4D0DB] text-[#011836] hover:bg-[#EDF1F5] hover:text-[#011836]"
            data-testid={`upload-${testid}-btn`}
          >
            <Upload className="h-3.5 w-3.5 mr-1.5" />
            {uploading ? "Uploading…" : path ? "Replace" : "Upload"}
          </Button>
          {downloadPath && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={handleDownload}
              disabled={downloading}
              title={`Download the ${label.toLowerCase()} file`}
              className="border-[#C4D0DB] text-[#33475C] hover:text-[#0088CC] hover:border-[#0088CC]/50 hover:bg-[#E6F2FA]/40"
              data-testid={`download-${testid}-btn`}
            >
              <Download className={`h-3.5 w-3.5 ${downloading ? "animate-pulse" : ""}`} />
            </Button>
          )}
          {path && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={onRemove}
              className="border-[#C4D0DB] text-[#5B6B7C] hover:text-[#EF4444] hover:bg-[#FDECEC] hover:border-[#EF4444]"
              data-testid={`remove-${testid}-btn`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
          <span className="ml-auto text-[10px] text-[#5B6B7C]">{hint}</span>
        </div>
      </div>
    </div>
  );
}
